// The account's achievements, drawn (ACHIEVEMENTS.md §11): a found badge lit
// in its family's color, an open one outlined with its progress ring, a hint
// as a dashed silhouette with its riddle, and a family's secrets as a count.
// Shared by the wall, the account page's section and the moment.
//
// DETAIL LEVELS (§11). Simple shows no numbers: no "2 of 3", a ring without a
// figure, "More secrets" for the count, no date. Standard adds the counts, the
// date and the league on a found one. Everything adds nothing more yet.
// TODO(R4): Everything adds the stat line behind a found one and, once there
// are a hundred active accounts, rarity (§7, §11).

import type { ReactNode } from 'react';
import type { FoundAchievement, HintedAchievement, OpenAchievement } from '@fsp/domain';
import { useAtLeast } from '../../data/detail';
import { AchievementGlyph } from '../../kit/Icon';
import { Button, Caption, Stack, Text } from '../../kit/ui';
import '../../styles/screens/achievements.css';

/** One of eight hues for a family, by its id: stable, so a family keeps its color. */
export function hueOf(family: string): number {
  let h = 0;
  for (let i = 0; i < family.length; i++) h = (h * 31 + family.charCodeAt(i)) >>> 0;
  return h % 8;
}

/** "Oct 3", for the date a badge was found. */
export function foundOn(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Ring({ have, need }: { have: number; need: number }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const done = Math.max(0, Math.min(1, have / need));
  return (
    <svg className="fsp-ach-ring" viewBox="0 0 50 50" aria-hidden>
      <circle className="fsp-ach-ring-track" cx="25" cy="25" r={r} />
      <circle
        className="fsp-ach-ring-done"
        cx="25"
        cy="25"
        r={r}
        strokeDasharray={`${c * done} ${c}`}
      />
    </svg>
  );
}

function Glyph({ icon, ring }: { icon: string; ring?: { have: number; need: number } | null }) {
  return (
    <span className="fsp-ach-glyph">
      {ring ? <Ring have={ring.have} need={ring.need} /> : null}
      <AchievementGlyph icon={icon} size={22} />
    </span>
  );
}

interface TileProps {
  family: string;
  selected?: boolean;
  onOpen?: () => void;
}

function Tile({
  kind,
  family,
  selected,
  onOpen,
  label,
  children,
}: TileProps & { kind: string; label: string; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`fsp-ach-tile fsp-ach-hued ${kind}`}
      data-kind={kind}
      data-hue={hueOf(family)}
      aria-current={selected ? 'true' : undefined}
      aria-label={label}
      onClick={onOpen}
    >
      {children}
    </button>
  );
}

/** A found badge: lit, with its name and the start of its line. */
export function FoundTile({ a, ...rest }: TileProps & { a: FoundAchievement }) {
  return (
    // The words on the tile come first and in order, the state after them,
    // so what a screen reader says contains what the eye reads (WCAG 2.5.3).
    <Tile kind="found" label={`${a.name}. ${a.line} (found)`} {...rest}>
      <Glyph icon={a.icon} />
      <span className="fsp-ach-name">{a.name}</span> <span className="fsp-ach-sub">{a.line}</span>
    </Tile>
  );
}

/** An open one: outlined, what it asks, and how near (§7). */
export function OpenTile({ a, ...rest }: TileProps & { a: OpenAchievement }) {
  const counts = useAtLeast('standard');
  return (
    <Tile
      kind="open"
      label={`${a.name}. ${a.asks}${counts && a.progress ? ` ${a.progress.have} of ${a.progress.need}` : ''} (not found yet)`}
      {...rest}
    >
      <Glyph icon={a.icon} ring={a.progress} />
      <span className="fsp-ach-name">{a.name}</span> <span className="fsp-ach-sub">{a.asks}</span>
      {counts && a.progress ? (
        <span className="fsp-ach-count">
          {a.progress.have} of {a.progress.need}
        </span>
      ) : null}
    </Tile>
  );
}

/** A hidden one: a silhouette and its riddle, and who in my leagues found it. */
export function HintTile({ a, ...rest }: TileProps & { a: HintedAchievement }) {
  return (
    <Tile
      kind="hint"
      label={`A hidden achievement. ${a.hint}${a.foundBy ? ` ${a.foundBy.name} found this one.` : ''}`}
      {...rest}
    >
      <Glyph icon={a.icon} />
      {a.hint ? <span className="fsp-ach-sub fsp-ach-riddle">{a.hint}</span> : null}
      {a.foundBy ? <span className="fsp-ach-sub">{a.foundBy.name} found this one</span> : null}
    </Tile>
  );
}

/** A family's secrets, as a count (§18 4: a count is a promise; blank tiles are a wall of nothing). */
export function SecretsTile({ family, count }: { family: string; count: number }) {
  const counts = useAtLeast('standard');
  const said = counts ? `${count} secret${count === 1 ? '' : 's'}` : 'More secrets';
  return (
    <div
      className="fsp-ach-tile fsp-ach-hued secrets"
      data-kind="secrets"
      data-hue={hueOf(family)}
      aria-label={said}
    >
      <span className="fsp-ach-glyph">
        <AchievementGlyph icon="sparkle" size={22} />
      </span>
      <span className="fsp-ach-name">{said}</span>
    </div>
  );
}

/** What a badge says opened (§11): the badge large, the family, then by kind. */
export type Opened =
  | { kind: 'found'; a: FoundAchievement; familyName: string; familyId: string }
  | { kind: 'open'; a: OpenAchievement; familyName: string; familyId: string }
  | { kind: 'hint'; a: HintedAchievement; familyName: string; familyId: string };

export function AchievementDetail({
  opened,
  link,
}: {
  opened: Opened;
  /** Where an open one is tried, as this client's screen. */
  link?: { label: string; onGo: () => void } | null;
}) {
  const counts = useAtLeast('standard');
  const icon = opened.a.icon;
  const name = opened.kind === 'hint' ? 'A hidden one' : opened.a.name;
  return (
    <div className="fsp-ach-detail fsp-ach-hued" data-hue={hueOf(opened.familyId)}>
      <span data-kind={opened.kind}>
        <Glyph icon={icon} ring={opened.kind === 'open' ? opened.a.progress : null} />
      </span>
      <span className="fsp-ach-detail-family">{opened.familyName}</span>
      <span className="fsp-ach-detail-name">{name}</span>
      <div className="fsp-ach-detail-body">
        {opened.kind === 'found' ? (
          <Stack gap={2}>
            <Text block>{opened.a.line}</Text>
            {counts ? (
              <Caption>
                {[
                  opened.a.league?.name ?? null,
                  `Found ${foundOn(opened.a.earnedAt)}`,
                  opened.a.retro ? 'found late, for what already happened' : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Caption>
            ) : null}
            <Text tone="secondary" size="xs" block>
              {opened.a.asks}
            </Text>
            {opened.a.hint ? (
              <Text tone="secondary" size="xs" block>
                The hint was: <i>{opened.a.hint}</i>
              </Text>
            ) : null}
          </Stack>
        ) : opened.kind === 'open' ? (
          <Stack gap={2}>
            <Text block>{opened.a.asks}</Text>
            {counts && opened.a.progress ? (
              <Caption>
                {opened.a.progress.have} of {opened.a.progress.need}
              </Caption>
            ) : null}
            {link ? (
              <div>
                <Button size="xs" onClick={link.onGo}>
                  {link.label}
                </Button>
              </div>
            ) : null}
          </Stack>
        ) : (
          <Stack gap={2}>
            {opened.a.hint ? (
              <Text block>
                <i>{opened.a.hint}</i>
              </Text>
            ) : null}
            {opened.a.foundBy ? (
              <Text tone="secondary" size="xs" block>
                {opened.a.foundBy.name} found this one in {opened.a.foundBy.league}.
              </Text>
            ) : null}
          </Stack>
        )}
      </div>
    </div>
  );
}
